'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  Eye,
  EyeOff,
  Shield,
  ChevronRight,
  Loader2,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/components/providers/AuthProvider';
import {
  DEFAULT_AUTHENTICATED_PATH,
  canAccessPath,
  getFirstAllowedPath,
  getSafeNextPath,
} from '@/lib/auth-config';
import type { AuthUser, Login2faChallenge } from '@/lib/auth-api';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

type LoginStep = 'credentials' | 'otp';

const OTP_LENGTH = 6;
const OTP_TTL_SECONDS = 180;

const features = [
  { key: 'feat-pdf', icon: '📄', text: 'PDF to HTML5 Flipbook conversion' },
  { key: 'feat-gcs', icon: '☁️', text: 'Hosted on Google Cloud Storage' },
  { key: 'feat-otp', icon: '🔐', text: 'OTP-gated public access' },
  { key: 'feat-expiry', icon: '📅', text: 'Configurable expiry dates' },
];

export default function AdminLoginPageClient() {
  const { login, verifyLogin2fa, resendLogin2fa } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<LoginStep>('credentials');
  const [challenge, setChallenge] = useState<Login2faChallenge | null>(null);
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [otpError, setOtpError] = useState('');
  const [otpLocked, setOtpLocked] = useState(false);
  const [otpExpiresIn, setOtpExpiresIn] = useState(OTP_TTL_SECONDS);
  const [remainingSends, setRemainingSends] = useState(4);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    defaultValues: {
      email: "",
      password: "",
      remember: false,
    },
  });

  useEffect(() => {
    if (step !== 'otp' || otpExpiresIn <= 0) return;
    const timer = setTimeout(() => setOtpExpiresIn((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [step, otpExpiresIn]);

  const redirectAfterLogin = (nextUser: AuthUser) => {
    toast.success('Welcome back! Redirecting...');
    const safeNext = getSafeNextPath(
      searchParams.get('next'),
      DEFAULT_AUTHENTICATED_PATH
    );
    const destination = canAccessPath(nextUser, safeNext)
      ? safeNext
      : getFirstAllowedPath(nextUser, DEFAULT_AUTHENTICATED_PATH);
    console.log(`[Login] Login complete for ${nextUser.email}. Redirecting to ${destination}`);
    if (typeof window !== 'undefined') {
      window.location.replace(destination);
    } else {
      router.replace(destination);
    }
  };

  const applyChallenge = (next: Login2faChallenge) => {
    setChallenge(next);
    setOtp(Array(OTP_LENGTH).fill(''));
    setOtpError('');
    setOtpLocked(false);
    setOtpExpiresIn(next.expires_in_seconds || OTP_TTL_SECONDS);
    if (typeof next.remaining_sends === 'number') {
      setRemainingSends(next.remaining_sends);
    }
    setStep('otp');
  };

  const applyOtpFailure = (
    error: unknown,
    fallback: string,
    { alwaysToast = false }: { alwaysToast?: boolean } = {}
  ) => {
    const message = error instanceof ApiError ? error.message : fallback;
    const locked =
      error instanceof ApiError &&
      (error.status === 429 ||
        /too many invalid otp attempts/i.test(message));
    setOtpError(message);
    setOtpLocked(locked);
    if (alwaysToast || !locked) {
      toast.error(message);
    }
  };

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    console.log(`[Login] Submitting login for email: ${data.email}`);
    try {
      const result = await login(data.email.trim(), data.password);
      if (result.requires_2fa && result.challenge) {
        console.log('[Login] 2FA required for user');
        applyChallenge(result.challenge);
        toast.info(
          result.challenge.emailed === false
            ? 'OTP generated — check your email inbox (or server logs if SMTP is off)'
            : `OTP sent to ${result.challenge.email_hint}`
        );
        return;
      }
      redirectAfterLogin(result.user);
    } catch (error) {
      console.error('[Login] Login failed:', error);
      const message =
        error instanceof ApiError
          ? error.message
          : 'Unable to sign in. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value) || otpLocked) return;
    const next = [...otp];
    next[index] = value.slice(-1);
    setOtp(next);
    setOtpError('');
    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH);
    const next = [...otp];
    pasted.split('').forEach((char, i) => {
      next[i] = char;
    });
    setOtp(next);
    inputRefs.current[Math.min(pasted.length, OTP_LENGTH - 1)]?.focus();
  };

  const handleVerifyOtp = async () => {
    if (!challenge || otpLocked) return;
    const code = otp.join('');
    if (code.length < OTP_LENGTH) {
      setOtpError(`Enter all ${OTP_LENGTH} digits of your OTP`);
      return;
    }
    if (otpExpiresIn <= 0) {
      setOtpError('OTP has expired. Please resend a new one.');
      return;
    }

    setLoading(true);
    try {
      const nextUser = await verifyLogin2fa({
        challenge_id: challenge.challenge_id,
        email: challenge.email,
        otp: code,
      });
      redirectAfterLogin(nextUser);
    } catch (error) {
      applyOtpFailure(error, 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!challenge) return;
    if (remainingSends <= 0 && otpExpiresIn > 0 && !otpLocked) {
      toast.error(
        `Too many OTP requests. Please wait ${otpExpiresIn}s before trying again.`
      );
      return;
    }

    setLoading(true);
    try {
      const next = await resendLogin2fa({
        challenge_id: challenge.challenge_id,
        email: challenge.email,
      });
      applyChallenge(next);
      toast.success(
        next.emailed === false
          ? 'New OTP ready (check server logs / toast if SMTP is off)'
          : 'A new OTP has been sent'
      );
      inputRefs.current[0]?.focus();
    } catch (error) {
      applyOtpFailure(error, 'Failed to resend OTP. Please try again.', {
        alwaysToast: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setChallenge(null);
    setOtp(Array(OTP_LENGTH).fill(''));
    setOtpError('');
    setOtpLocked(false);
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-[52%] gradient-hero flex-col justify-between p-10 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 rounded-full bg-white" />
          <div className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-white" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-white" />
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <AppLogo size={52} />
            <div>
              <h2 className="text-xl font-800 text-white leading-tight">Birla Open Minds</h2>
              <p className="text-orange-200 text-xs">
                International School — Digital Library
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h1 className="text-4xl font-800 text-white leading-tight mb-4">
              Transform PDFs into
              <br />
              Interactive Flipbooks
            </h1>
            <p className="text-orange-100 text-lg leading-relaxed">
              Manage your school&apos;s digital book library. Convert, host, and
              share interactive flipbooks with parents, schools, and partners.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {features.map((f) => (
              <div
                key={f.key}
                className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20"
              >
                <span className="text-xl">{f.icon}</span>
                <span className="text-white text-sm font-500">{f.text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-2 bg-white/10 rounded-xl px-4 py-3 border border-white/20">
            <Shield size={16} className="text-orange-200" />
            <span className="text-orange-100 text-sm">
              Role-based access control — Super Admin & Admin roles
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-6 py-10 bg-background">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={40} />
            <div>
              <span className="font-800 text-base text-foreground block leading-tight">Birla Open Minds</span>
              <span className="text-xs text-muted-foreground">International School</span>
            </div>
          </div>

          {step === 'credentials' ? (
            <>
              <div className="mb-8">
                <h2 className="text-2xl font-700 text-foreground mb-1">
                  Admin Sign In
                </h2>
                <p className="text-muted-foreground text-sm">
                  Sign in to manage your flipbook library
                </p>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="flipbook.admin@yopmail.com"
                    className={`w-full px-4 py-2.5 text-sm bg-input border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all placeholder:text-muted-foreground ${
                      errors.email ? 'border-red-400' : 'border-border'
                    }`}
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Enter a valid email address',
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      className={`w-full px-4 py-2.5 pr-10 text-sm bg-input border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all placeholder:text-muted-foreground ${
                        errors.password ? 'border-red-400' : 'border-border'
                      }`}
                      {...register('password', {
                        required: 'Password is required',
                        minLength: {
                          value: 8,
                          message: 'Password must be at least 8 characters',
                        },
                      })}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="w-4 h-4 rounded border-border text-primary focus:ring-ring"
                      {...register('remember')}
                    />
                    <span className="text-sm text-muted-foreground">
                      Remember me
                    </span>
                  </label>
                  <Link
                    href="/forgot-password"
                    className="text-sm text-primary hover:underline font-500"
                  >
                    Forgot password?
                  </Link>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg font-600 text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ minHeight: '42px' }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Signing in...</span>
                    </>
                  ) : (
                    <>
                      <span>Sign In to Admin Portal</span>
                      <ChevronRight size={16} />
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleBackToCredentials}
                className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline mb-8 font-500"
              >
                <ArrowLeft size={14} />
                Back to sign in
              </button>

              <div className="mb-8">
                <h2 className="text-2xl font-700 text-foreground mb-1">
                  Two-factor verification
                </h2>
                <p className="text-muted-foreground text-sm">
                  Enter the {OTP_LENGTH}-digit code sent to{' '}
                  <span className="font-600 text-foreground">
                    {challenge?.email_hint || challenge?.email}
                  </span>
                </p>
              </div>

              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-600 text-foreground mb-1.5">
                    Verification code
                  </label>
                  <div
                    className="flex items-center justify-between gap-2"
                    onPaste={otpLocked ? undefined : handleOTPPaste}
                  >
                    {otp.map((digit, index) => (
                      <input
                        key={`otp-${index}`}
                        ref={(el) => {
                          inputRefs.current[index] = el;
                        }}
                        type="text"
                        inputMode="numeric"
                        autoComplete={index === 0 ? 'one-time-code' : 'off'}
                        maxLength={1}
                        value={digit}
                        disabled={otpLocked || loading}
                        onChange={(e) => handleOTPChange(index, e.target.value)}
                        onKeyDown={(e) => handleOTPKeyDown(index, e)}
                        className={`w-11 h-11 sm:w-12 sm:h-12 text-center text-lg font-700 bg-input border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring transition-all disabled:opacity-60 disabled:cursor-not-allowed ${
                          otpError
                            ? 'border-red-400'
                            : digit
                              ? 'border-primary text-primary'
                              : 'border-border'
                        }`}
                        aria-label={`OTP digit ${index + 1}`}
                      />
                    ))}
                  </div>
                  {otpError ? (
                    <p className="text-red-500 text-xs mt-1">{otpError}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Expires in{' '}
                      <span className="font-600 text-foreground">
                        {Math.floor(Math.max(otpExpiresIn, 0) / 60)}:
                        {String(Math.max(otpExpiresIn, 0) % 60).padStart(2, '0')}
                      </span>
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={handleBackToCredentials}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Change account
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={loading}
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-500 disabled:text-muted-foreground disabled:no-underline disabled:cursor-not-allowed"
                  >
                    <RefreshCw size={14} />
                    Resend OTP
                  </button>
                </div>

                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={
                    loading ||
                    otpLocked ||
                    otp.join('').length < OTP_LENGTH
                  }
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg font-600 text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ minHeight: '42px' }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Verifying...</span>
                    </>
                  ) : (
                    <>
                      <span>Verify & continue</span>
                      <ChevronRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </>
          )}

          <p className="text-center text-xs text-muted-foreground mt-6">
            Public flipbook access?{' '}
            <Link
              href="/otp-verification"
              className="text-primary hover:underline font-500"
            >
              Verify with OTP
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
